/* eslint-disable @typescript-eslint/no-explicit-any */
import { sharedMessages } from "#components/composables/intl"
import type { S } from "#resources/lib"
import { Email } from "#resources/lib/schema"
import { Effect, HttpClient, Option } from "@/utils/prelude"
import { initializeAsync, makeIntl } from "@effect-app/vue"
import { useIntlKey } from "@effect-app/vue-components"
import { customSchemaErrors } from "@effect-app/vue/form"
import { SemanticResourceAttributes } from "@opentelemetry/semantic-conventions"
import { ApiClientFactory } from "effect-app/client/apiClientFactory"
import { FetchHttpClient } from "effect/http"
import * as Layer from "effect/Layer"
import { Reactivity } from "effect/reactivity"
import { store } from "~/composables/userStore"
import { annotateTags, SentrySdkLive, WebSdkLive } from "~/utils/observability"

const messages = sharedMessages

export const versionMatch = ref(true)
/** App locale: single source of truth for makeIntl and the Vuetify locale (see plugins/vuetify.ts) */
export const locale = ref("de" as "de" | "en")

async function makeRuntime(
  beVersion: string,
  apiUrl: string,
  env: string,
  serviceName: string,
  isRemote: boolean,
  disableTracing: boolean
) {
  const apiLayers = ApiClientFactory
    .layer({
      url: apiUrl,
      headers: Option.none()
    })
    .pipe(
      Layer.provide(
        Layer.effect(
          HttpClient.HttpClient,
          Effect.gen(function*() {
            const client = yield* HttpClient.HttpClient
            return client.pipe(
              HttpClient.tapRequest(() => {
                const tags = {
                  //  "device.id": deviceId,
                  "user.id": store.user?.id
                }
                annotateTags(tags)
                return Effect.annotateCurrentSpan(tags)
              }),
              HttpClient.tap((r) =>
                Effect.sync(() => {
                  const remoteFeVersion = r.headers["x-be-version"]
                  if (remoteFeVersion) {
                    versionMatch.value = beVersion === remoteFeVersion
                  }
                })
              )
            )
          })
        )
      ),
      Layer.provide(FetchHttpClient.layer),
      Layer.provide(
        Layer.succeed(FetchHttpClient.RequestInit, { credentials: "include" })
      )
    )

  const otelAttrs = {
    serviceName,
    serviceVersion: beVersion,
    attributes: {
      [SemanticResourceAttributes.DEPLOYMENT_ENVIRONMENT]: env
      // ["fe.device.id"]: deviceId,
    }
  }

  const globalLayers = Layer
    .mergeAll(apiLayers, Reactivity.layer)
    .pipe(Layer.provideMerge(
      disableTracing
        ? Layer.empty
        : isRemote
        ? SentrySdkLive(otelAttrs, env)
        : WebSdkLive(otelAttrs)
    )) // .pipe(Layer.provideMerge(Logger.minimumLogLevel(LogLevel.Debug)))

  const rt = await initializeAsync(globalLayers)

  return Object.assign(rt, { globalLayers })
}

// TODO: make sure the runtime provides these
export type RT = ApiClientFactory

export default defineNuxtPlugin(async (nuxtApp) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const config = useRuntimeConfig()
  const isRemote = config.public.env !== "local-dev"
  const runtime = await makeRuntime(
    config.public.beVersion,
    config.public.apiUrl || "http://" + window.location.hostname + ":4200",
    config.public.env,
    config.public.serviceName,
    isRemote,
    !isRemote && !config.public.telemetry
  )

  const { useIntl } = makeIntl(messages, locale)

  watch(
    locale,
    () => {
      customSchemaErrors.value = new Map<
        S.AST.AST,
        (message: string, e: unknown, v: unknown) => string
      >([[Email.ast, () => "Der eingegebene Wert ist kein gültiger email"]])
    },
    { immediate: true }
  )

  // for OmegaForm
  nuxtApp.vueApp.provide(useIntlKey, useIntl as ReturnType<typeof makeIntl>["useIntl"])

  return { provide: { runtime, useIntl } }
})
